import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, setSessionExpiredHandler } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = guest
  const userRef = useRef(undefined);
  userRef.current = user;

  useEffect(() => {
    // Ritorno dal login Google: lo scambio session_id lo gestisce /auth/callback, qui non controllo /me
    // (user va a null, non lasciato in caricamento: altrimenti la pagina di ritorno resterebbe bloccata)
    if (window.location.hash?.includes("session_id=")) { setUser(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/me", { _skipRefresh: true });
        setUser(data);
      } catch {
        // Access token scaduto ma refresh ancora valido: rinnova in silenzio
        try {
          await api.post("/auth/refresh", null, { _skipRefresh: true });
          const { data } = await api.get("/auth/me", { _skipRefresh: true });
          setUser(data);
        } catch {
          setUser(null);
        }
      }
    })();
    setSessionExpiredHandler(() => {
      if (userRef.current) {
        setUser(null);
        sessionStorage.setItem("wanted_session_expired", "1");
        window.location.href = "/login";
      }
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) return data; // doppia verifica admin: il contesto si apre solo dopo il codice
    setUser(data);
    return data;
  };

  // Secondo passo del login admin con Google Authenticator attivo
  const verify2fa = async (pendingToken, code) => {
    const { data } = await api.post("/auth/2fa/verify", { pending_token: pendingToken, code });
    setUser(data);
    return data;
  };

  // Login Google: lo scambio del session_id avviene qui, poi la sessione è quella normale del sito
  const googleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data);
    return data;
  };

  // Login con passkey (Face ID / impronta): il telefono firma la sfida e apre la sessione, senza password
  const passkeyLogin = async () => {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const { data: opts } = await api.post("/passkeys/authentication/options");
    const assertion = await startAuthentication({ optionsJSON: opts });
    const { data } = await api.post("/passkeys/authentication/verify", assertion);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, login, verify2fa, googleSession, passkeyLogin, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
